import {
  action,
  computed,
  makeAutoObservable,
  observable,
  reaction,
} from "mobx";
import { CheckOutDisplayTypes, UpsertCheckoutActiveStep } from "../enums";
import CountryStore from "./CountryStore";
import { AuthService, ToastService } from "@mindmemobile/mm-shared-global";
import { CheckoutDetailPageModel } from "../models/CheckoutDetailPageModel";
import { CheckOutSellPageModel } from "../models/CheckOutSellPageModel";
import { CheckOutAfterPayModel } from "../models/CheckOutAfterPayModel";
import {
  BasicColors,
  CheckoutBrandingModel,
  CheckoutOptionsPageModel,
  CustomColors,
  DonationCheckoutModel,
} from "../models/CheckoutOptionsPageModel";
import {
  CheckoutPaymentFormModel,
  CheckoutPaymentPageModel,
} from "../models/CheckoutPaymentPageModel";
import {
  MerchantAccount,
  MerchantAccountWithPaySettingsModel,
  PaymentSettings,
} from "../models/MerchantApplicationInputModel";
import { SellerModel, withSellerPolicyCopy } from "../models/SellerModels";
import { ItemDetailInputModel } from "../models/ItemDetailInputModel";
import { DonationPricingPageInputsModel } from "../models/DonationAndItemModel";
import { CustomerFacingCheckOutModel } from "../models/PurchaseModel";
import { CustomerFacingCheckOutItemModel } from "../models/PriceUtility";
import {
  IAPICheckOutSaveUpdateModel,
  IAPIRelatedCheckoutGridModel,
} from "../interfaces/IAPICheckoutModel";
import { MediaModel } from "../models/MediaModel";
import ListInterestTagStore from "./ListInterestTagStore";
import SellerService from "../services/SellerService";
import { IAPICreateSellerModel } from "../interfaces/SellerInterfaces";

export default class CheckOutStore {
  @observable activeStep: UpsertCheckoutActiveStep;
  @observable detailPage: CheckoutDetailPageModel;
  @observable sellPage: CheckOutSellPageModel;
  @observable headerCaption: string;
  @observable isSaveUpdateInProgress: boolean = false;
  @observable afterPayPage: CheckOutAfterPayModel;
  @observable paymentPageData: CheckoutPaymentPageModel;
  @observable optionsPage: CheckoutOptionsPageModel;
  @observable merchantAccounts: MerchantAccount[] = [];
  @observable sellers: SellerModel[] = [];
  /* Sellers whose terms / return policy copy has been fetched from Seller/Get.
     GetSellerNames, which fills `sellers`, returns the seller's identity only,
     so a seller resolved from that list always looks like it has no policies. */
  @observable policyResolvedSellerIds: string[] = [];
  /* The seller this checkout was loaded with. Its saved policy choices were made deliberately, so
     they survive even if the seller's copy has since been emptied in Payment Settings - resetting
     them on load would hide the selection and, on the next save, quietly rewrite it. Picking a
     different seller is another matter: those choices were never made for that seller. */
  @observable sellerTermsBaselineSellerId: string | null = null;
  /* Whether the checkout was LOADED with each policy on display. Drives whether the builder offers
     the dropdown for a seller that has no copy. Deliberately a snapshot, not a read of the live
     value: keying off the current value would make the dropdown delete itself the moment the user
     picked "Do Not Display" in it, with no way back. */
  @observable sellerTermsWasConfigured: boolean = false;
  @observable sellerReturnPolicyWasConfigured: boolean = false;
  @observable gridData: CheckoutRelatedGridModel[] = [];
  @observable isDonation: boolean = false;
  @observable donationdetail: ItemDetailInputModel;
  @observable donationPricingData: DonationPricingPageInputsModel;
  listInterestTagStore: ListInterestTagStore;

  constructor(data?: CheckOutStore) {
    makeAutoObservable(this);
    this.detailPage = new CheckoutDetailPageModel();
    this.activeStep = UpsertCheckoutActiveStep.Detail;
    this.headerCaption = "Detail";
    this.sellPage = new CheckOutSellPageModel();
    this.afterPayPage = new CheckOutAfterPayModel();
    this.paymentPageData = new CheckoutPaymentPageModel();
    this.optionsPage = new CheckoutOptionsPageModel();
    this.donationPricingData = new DonationPricingPageInputsModel();
    this.donationdetail = new ItemDetailInputModel();
    this.listInterestTagStore = new ListInterestTagStore();
    if (data) this.hydrate(data);

    reaction(
      () => this.activeStep,
      () => this.updateStateBasedOnActiveStatusChange(),
    );
  }

  hydrate(data: CheckOutStore) {
    this.activeStep = data.activeStep;
    this.headerCaption = data.headerCaption;
    this.detailPage = new CheckoutDetailPageModel(data.detailPage);
    this.sellPage = new CheckOutSellPageModel(data.sellPage);

    this.afterPayPage = new CheckOutAfterPayModel({
      ...data.afterPayPage,
    });

    this.paymentPageData = new CheckoutPaymentPageModel({
      ...data.paymentPageData,
      formData: new CheckoutPaymentFormModel({
        ...data.paymentPageData.formData,
      }),
    });
    this.optionsPage = new CheckoutOptionsPageModel({
      ...data.optionsPage,
      brandingData: new CheckoutBrandingModel({
        ...data.optionsPage.brandingData,
        basicColors: new BasicColors({
          ...data.optionsPage.brandingData.basicColors,
        }),
        customColors: new CustomColors({
          ...data.optionsPage.brandingData.customColors,
        }),
        donationCheckout: new DonationCheckoutModel({
          ...data.optionsPage.brandingData.donationCheckout,
        }),
      }),
    });
    this.donationPricingData = new DonationPricingPageInputsModel(
      data.donationPricingData,
    );
    this.donationdetail = new ItemDetailInputModel(data.donationdetail);
  }

  /**
   * Payment settings for the merchant account selected on the Detail step.
   *
   * CheckoutDetailPageModel.deserializeAPIModel only maps ids/name/gateway from
   * the checkout edit API, so the selected account can arrive without payment
   * settings, in which case PaymentSettings defaults to cards.isEnabled false
   * and surchargePercentage "0.00" and anything depending on those settings
   * (surcharge, in particular) behaves as if the merchant had no card
   * processing at all. DetailPage.getMerchantAccounts backfills the settings
   * once the merchant account list loads; this getter is the safety net for
   * anything that reads them before or without that backfill, resolving from
   * the loaded list the same way CheckoutPayment does to filter payment
   * methods.
   */
  @computed
  get selectedMerchantAccountPaymentSettings(): PaymentSettings {
    const selected = this.detailPage.selectedMerchantAccount;
    const matched = this.merchantAccounts.find(
      (account) =>
        (!!selected.merchantAccountId &&
          account.merchantAccountId === selected.merchantAccountId) ||
        (!!selected.id && account.id === selected.id),
    );
    return matched?.paymentSettings || selected.paymentSettings;
  }

  @computed
  get customerFacingModel(): CustomerFacingCheckOutModel {
    const data: Partial<CustomerFacingCheckOutModel> = {
      isDeleted: false,
      isActive: this.detailPage.isActive,
      items: this.sellPage.items.map((item) =>
        // donationdetail is passed so donation rows can resolve their
        // "Allow Surcharge" toggle, which the available-items API does not put
        // on itemDetails. See CheckOutItemsModel.allowsSurcharge.
        CustomerFacingCheckOutItemModel.deserializeFromCheckOutItemsModel(
          item,
          this.donationdetail,
        ),
      ),
      recommendedItem: this.sellPage.recommendedItem
        ? this.sellPage.recommendedItem
        : undefined,
      paymentDetails: this.paymentPageData,
      afterPayDetails: this.afterPayPage ? this.afterPayPage : null,
      options: this.optionsPage,
      showSingleItemPrice: false,
      displayType: this.detailPage
        .selectedDisplayType as keyof typeof CheckOutDisplayTypes,
      allowIncludedItems: this.sellPage.allowIncludedItems,
      allowRequiredItems: this.sellPage.allowRequiredItems,
      allowRecommendedItems: this.sellPage.allowRecommendedItems,
      groupByBillingFrequency: this.sellPage.groupByBillingFrequency,
      merchantAccount: new MerchantAccountWithPaySettingsModel({
        name: this.detailPage.selectedMerchantAccount.name,
        gateway: this.detailPage.selectedMerchantAccount.gateway,
        merchantAccountId:
          this.detailPage.selectedMerchantAccount.merchantAccountId,
        applicationId: this.detailPage.selectedMerchantAccount.applicationId,
        paymentSettings: this.selectedMerchantAccountPaymentSettings,
        collectJsKey: this.detailPage.selectedMerchantAccount.collectJSKey,
      }),
      isDonationOnly: this.detailPage.isDonationOnly,
      /* The resolved seller, so the preview renders the policy copy under
         exactly the same condition the Terms panel offers it. */
      seller: this.selectedSellerDetails || this.detailPage.selectedSeller,
      donationCheckout: this.optionsPage.brandingData.donationCheckout,
      donationDetailPage: this.donationdetail,
      donationPricingPageData: this.donationPricingData,
      customOption: this.sellPage.customoption
        ? this.sellPage.customoption
        : undefined,
    };

    return new CustomerFacingCheckOutModel(data);
  }

  @action
  updateDonationValue(val: boolean) {
    this.isDonation = val;
  }

  @action
  updateDonationData(
    detail: ItemDetailInputModel,
    priceData: DonationPricingPageInputsModel,
    isDonation: boolean,
  ) {
    this.donationPricingData = priceData;
    this.donationdetail = detail;
    this.isDonation = isDonation;
  }

  deserializeAPIResponse(data: IAPIRelatedCheckoutGridModel[]): void {
    ("");
    this.gridData =
      (data &&
        data.map((purchaseLink: any) =>
          CheckoutRelatedGridModel.deserializeAPIModel(purchaseLink),
        )) ||
      [];
  }

  @action
  updateMerchantAccounts(accounts: MerchantAccount[]) {
    this.merchantAccounts = accounts;
  }

  @action
  updateSellers(sellers: SellerModel[]) {
    /* The reloaded list carries no policy copy, so replacing outright drops
       whatever was merged into the previous entries and hides the Terms panel
       until the per-seller fetch lands again. Carry the known copy over, and
       still clear the resolved ids so edits made since are picked up. */
    const previous = this.sellers;
    this.sellers = sellers.map((seller) =>
      withSellerPolicyCopy(
        seller,
        previous.find((known) => known.id === seller.id),
      ),
    );
    this.policyResolvedSellerIds = [];
  }

  @action
  updateActiveStep(activeStep: UpsertCheckoutActiveStep) {
    this.activeStep = activeStep;
  }

  @action
  updateDetailPage(data: CheckoutDetailPageModel) {
    this.detailPage = new CheckoutDetailPageModel(data);
  }

  @action
  updateStateBasedOnActiveStatusChange() {
    switch (this.activeStep) {
      case UpsertCheckoutActiveStep.Detail:
        this.headerCaption = "Detail";
        break;
      case UpsertCheckoutActiveStep.Sell:
        this.headerCaption = "Sell";
        break;
      case UpsertCheckoutActiveStep.Pay:
        this.headerCaption = "Pay";
        break;
      case UpsertCheckoutActiveStep.AfterPay:
        this.headerCaption = "After Pay";
        break;
      case UpsertCheckoutActiveStep.Options:
        this.headerCaption = "Checkout Options";
        break;
    }
  }

  @action
  validate(step?: UpsertCheckoutActiveStep): boolean {
    const isTabChange = step ? true : false;
    const stepToValidate = isTabChange ? step : this.activeStep;
    switch (stepToValidate) {
      case UpsertCheckoutActiveStep.Detail:
        const isValidDetailPage = this.detailPage.validate();
        this.updateDetailPage(new CheckoutDetailPageModel(this.detailPage));
        return isValidDetailPage;
      case UpsertCheckoutActiveStep.Sell:
        const isValidSellPage = this.sellPage.validate();
        if (this.detailPage.isDonationOnly) {
          const hasNonDonationItem = this.sellPage.items.some(
            (item) => item.itemDetails.type !== "Donation",
          );
          if (hasNonDonationItem) {
            ToastService.showError(
              'Invalid items: "Product, Service or Membership" items are not allowed.',
            );
            return false;
          }
        }
        if (!this.detailPage.isDonationOnly) {
          const hasDonationItem = this.sellPage.items.some(
            (item) => item.itemDetails.type === "Donation",
          );
          if (hasDonationItem) {
            ToastService.showError(
              'Invalid items: "Donation" items are not allowed.',
            );
            return false;
          }
          if (
            this.detailPage.selectedDisplayType ===
            CheckOutDisplayTypes.SingleItem
          ) {
            if (this.sellPage.items.length !== 1) {
              ToastService.showError("Please select an item.");
              return false;
            }
          }
          if (
            this.detailPage.selectedDisplayType ===
            CheckOutDisplayTypes.SingleItemWithMultiplePrices
          ) {
            const uniqueItemIds = new Set(
              this.sellPage.items.map((item) => item.itemId),
            );
            const uniquePriceIds = new Set(
              this.sellPage.items.map((item) => item.priceId),
            );
            if (uniqueItemIds.size !== 1 || uniquePriceIds.size <= 1) {
              ToastService.showError(
                "Please select single item with different prices",
              );
              return false;
            }
          }
          if (
            this.detailPage.selectedDisplayType ===
              CheckOutDisplayTypes.MultipleItems ||
            this.detailPage.selectedDisplayType ===
              CheckOutDisplayTypes.MultipleItemsInbundle
          ) {
            const uniqueItemIds = new Set(
              this.sellPage.items.map((item) => item.itemId),
            );
            const uniqueItemPriceCombination = new Set(
              this.sellPage.items.map(
                (item) => `${item.itemId}-${item.priceId}`,
              ),
            );

            if (uniqueItemIds.size === 1) {
              ToastService.showError(
                "More than one item is required for the layout selected.",
              );
              return false;
            }
            if (this.sellPage.items.length > 4) {
              ToastService.showError("A maximum of 4 items is allowed.");
              return false;
            }
            if (
              uniqueItemPriceCombination.size !== this.sellPage.items.length
            ) {
              ToastService.showError(
                "Invalid selection: Items must have unique prices.",
              );
              return false;
            }
          }
        }

        this.updateSellPage(new CheckOutSellPageModel(this.sellPage));
        return isValidSellPage;
      case UpsertCheckoutActiveStep.AfterPay:
        const isValidAfterPayPage = this.afterPayPage.validate();
        this.updateAfterPayPage(new CheckOutAfterPayModel(this.afterPayPage));
        return isValidAfterPayPage;
      case UpsertCheckoutActiveStep.Pay:
        const isValidPayPage = this.paymentPageData.validate();
        this.updatePaymentPage(
          new CheckoutPaymentPageModel(this.paymentPageData),
        );
        return isValidPayPage;
      case UpsertCheckoutActiveStep.Options:
        let isValidOptions = this.optionsPage.validate();
        this.updateOptionsPage(new CheckoutOptionsPageModel(this.optionsPage));
        if (!isValidOptions) {
          ToastService.showError(
            "Please fill all the required fields in the Options step before saving",
          );
        } else if (
          this.paymentPageData.isPromotionCodes &&
          this.paymentPageData.promoCodeSelectionType === "SelectCodes" &&
          this.paymentPageData.promocodes.length === 0
        ) {
          ToastService.showError("Please select at least one promotion code.");
          isValidOptions = false;
        }
        return isValidOptions;
      default:
        return false;
    }
  }

  @action
  validatePreviousSteps(currentStep: UpsertCheckoutActiveStep): {
    isValid: boolean;
    errorStep?: UpsertCheckoutActiveStep;
  } {
    const steps = [
      UpsertCheckoutActiveStep.Detail,
      UpsertCheckoutActiveStep.Sell,
      UpsertCheckoutActiveStep.Pay,
      UpsertCheckoutActiveStep.AfterPay,
      UpsertCheckoutActiveStep.Options,
    ];

    const currentStepIndex = steps.indexOf(currentStep);
    for (let i = 0; i < currentStepIndex; i++) {
      const step = steps[i];
      if (!this.validate(step)) {
        return { isValid: false, errorStep: step };
      }
    }
    return { isValid: true };
  }

  @action
  reset() {
    this.detailPage = new CheckoutDetailPageModel({ isActive: true });
    this.activeStep = UpsertCheckoutActiveStep.Detail;
    this.headerCaption = "Detail";
    this.isSaveUpdateInProgress = false;
    this.sellPage = new CheckOutSellPageModel();
    this.afterPayPage = new CheckOutAfterPayModel();

    this.paymentPageData = new CheckoutPaymentPageModel();
    this.optionsPage = new CheckoutOptionsPageModel();
    /* The store outlives a single checkout, so these have to go too. Left behind, the baseline from
       the checkout just edited would exempt that seller from the reset on the NEXT (new) checkout,
       and the snapshots would keep offering the dropdowns for a seller with no copy. */
    this.sellerTermsBaselineSellerId = null;
    this.sellerTermsWasConfigured = false;
    this.sellerReturnPolicyWasConfigured = false;
  }

  @action
  toggleSaveUpdateInProgress() {
    this.isSaveUpdateInProgress = !this.isSaveUpdateInProgress;
  }

  updateSellPage(data: CheckOutSellPageModel) {
    this.sellPage = new CheckOutSellPageModel(data);
  }

  updateAfterPayPage(data: CheckOutAfterPayModel) {
    this.afterPayPage = new CheckOutAfterPayModel(data);
  }

  @action
  updatePaymentPage(data: CheckoutPaymentPageModel) {
    this.paymentPageData = new CheckoutPaymentPageModel(data);
  }

  /**
   * Seeds this checkout's surcharge from the Credit Card defaults of the
   * merchant account that was just picked on the Detail step.
   *
   * Only ever called from a merchant account selection, never while loading a
   * saved checkout, so editing a merchant account afterwards cannot rewrite a
   * checkout that already holds its own values.
   */
  @action
  applyMerchantSurchargeDefaults(account?: MerchantAccount) {
    if (!account) return;
    this.updatePaymentPage(
      new CheckoutPaymentPageModel({
        ...this.paymentPageData,
        ...account.surchargeDefaults(),
      }),
    );
  }

  /**
   * Edit mode fallback: a saved checkout that comes back from the API without
   * a surcharge percentage (never set, or zeroed by a save made with the
   * surcharge switched off) keeps the selected merchant account's rate, so the
   * field is never empty. Only the rate is filled in - the checkout's own
   * "Add surcharge" and "Ask donor first" stay exactly as they were saved.
   */
  @action
  applyMerchantSurchargePercentageFallback() {
    if (this.paymentPageData.surchargePercentageValue > 0) return;
    const { surchargePercentage } =
      this.selectedMerchantAccountPaymentSettings.surchargeDefaults();
    if (!(parseFloat(surchargePercentage || "0") > 0)) return;
    this.updatePaymentPage(
      new CheckoutPaymentPageModel({
        ...this.paymentPageData,
        surchargePercentage,
      }),
    );
  }

  /**
   * The selected seller as GetSellerNames currently knows it, carrying the
   * policy copy from wherever it is available - the names list returns the
   * seller's identity only. Falls back to the seller stored on the checkout
   * while the list is still loading, or when the seller is no longer in it.
   */
  @computed
  get selectedSellerDetails(): SellerModel | undefined {
    /* Undefined until the detail page has a seller - callers guard for it. */
    const selected = this.detailPage?.selectedSeller;
    if (!selected?.id) return selected;
    const fromList = this.sellers.find((seller) => seller.id === selected.id);
    if (!fromList) return selected;
    /* Identity comes from the list, the policy copy from whichever of the two
       has it. GetSellerNames returns no copy at all, so taking the list entry
       outright discards the copy the checkout was loaded with, which hides the
       Terms dropdowns for a seller whose terms and return policy do exist. */
    return withSellerPolicyCopy(fromList, selected);
  }

  /**
   * True once the selected seller's policy copy is known: either it is already
   * on the seller, or Seller/Get has answered for it. Until then the Terms
   * panel has nothing to go on, so it neither shows the dropdowns nor rewrites
   * what the checkout saved.
   */
  @computed
  get isSelectedSellerPolicyResolved(): boolean {
    const seller = this.selectedSellerDetails;
    if (!seller?.id) return true;
    return (
      seller.hasTermsAndConditions ||
      seller.hasReturnPolicy ||
      this.policyResolvedSellerIds.includes(seller.id)
    );
  }

  /**
   * Pulls the selected seller's terms and return policy from Seller/Get.
   *
   * The builders resolve the seller from GetSellerNames, which returns the
   * seller's identity only - no policy copy - so both flags read false there
   * and the Terms panel stays hidden however the seller is set up. Seller/Get
   * is the endpoint that carries the copy (it is what Payment Settings edits),
   * so fetch it once per seller and merge the copy in.
   */
  @action
  loadSelectedSellerPolicies() {
    const sellerId = this.detailPage?.selectedSeller?.id;
    if (!sellerId || this.policyResolvedSellerIds.includes(sellerId)) return;
    SellerService.getSeller(AuthService.authData.accountId, sellerId).subscribe(
      {
        next: (response) => {
          if (response.result) {
            this.mergeSellerPolicies(sellerId, response.result);
          }
        },
        /* Left unresolved on failure so the next visit to the step retries,
           rather than the seller being treated as having no policies. */
        error: () => undefined,
      },
    );
  }

  @action
  mergeSellerPolicies(sellerId: string, data: IAPICreateSellerModel) {
    const policies = {
      termsAndConditions: data.termsAndConditions || "",
      termsAndConditionsEditorRawContent:
        data.termsAndConditionsEditorRawContent || "",
      returnPolicy: data.returnPolicy || "",
      returnPolicyEditorRawContent: data.returnPolicyEditorRawContent || "",
    };
    this.sellers = this.sellers.map((seller) =>
      seller.id === sellerId
        ? new SellerModel({ ...seller, ...policies })
        : seller,
    );
    /* Also on the checkout's own seller: it is what selectedSellerDetails falls
       back to while the list is still loading. */
    if (this.detailPage?.selectedSeller?.id === sellerId) {
      this.updateDetailPage(
        new CheckoutDetailPageModel({
          ...this.detailPage,
          selectedSeller: new SellerModel({
            ...this.detailPage.selectedSeller,
            ...policies,
          }),
        }),
      );
    }
    if (!this.policyResolvedSellerIds.includes(sellerId)) {
      this.policyResolvedSellerIds = [
        ...this.policyResolvedSellerIds,
        sellerId,
      ];
    }
    this.applySellerTermsAvailability();
  }

  /**
   * Terms and the return policy can only be presented at checkout when the
   * selected seller actually has that copy. Whatever the seller is missing is
   * forced back to "Do Not Display", so switching seller - or clearing the copy
   * in Payment Settings - can never leave a checkout saved as displaying, or
   * requiring acceptance of, something that does not exist.
   */
  @action
  applySellerTermsAvailability() {
    const seller = this.selectedSellerDetails;
    if (!seller) return;
    /* Nothing is known about the seller's policies yet, so resetting now would
       wipe a saved selection the seller can in fact present. */
    if (!this.isSelectedSellerPolicyResolved) return;
    /* Still the seller this checkout was saved with, so its stored choices stand. */
    if (seller.id && seller.id === this.sellerTermsBaselineSellerId) return;
    const sellerTermsAndConditions = seller.hasTermsAndConditions
      ? this.paymentPageData.sellerTermsAndConditions
      : "DoNotDisplay";
    const sellerReturnPolicy = seller.hasReturnPolicy
      ? this.paymentPageData.sellerReturnPolicy
      : "DoNotDisplay";
    if (
      sellerTermsAndConditions ===
        this.paymentPageData.sellerTermsAndConditions &&
      sellerReturnPolicy === this.paymentPageData.sellerReturnPolicy
    ) {
      return;
    }
    this.updatePaymentPage(
      new CheckoutPaymentPageModel({
        ...this.paymentPageData,
        sellerTermsAndConditions,
        sellerReturnPolicy,
      }),
    );
  }

  @action
  updateOptionsPage(data: CheckoutOptionsPageModel) {
    this.optionsPage = new CheckoutOptionsPageModel(data);
  }

  createAPIModel(id: string = ""): IAPICheckOutSaveUpdateModel {
    return {
      ...this.detailPage.createAPIModel(),
      id: id,
      accountId: AuthService.authData.accountId,
      payDetails: this.paymentPageData.createApiModel(),
      sellDetails: this.sellPage.createAPIModel(),
      afterPayDetails: this.afterPayPage.createAPIModel(),
      options: this.optionsPage.createAPIModel(this.detailPage.isDonationOnly),
      mediaPlacement:
        this.optionsPage.brandingData.donationCheckout.createAPIModel(),
      media: this.optionsPage.brandingData.donationCheckout.media[0]?.fullURL
        ? this.optionsPage.brandingData.donationCheckout.media[0].createAPIModel()
        : null,
      customOption: this.sellPage.customoption?.createAPIModel(),
      lists: this.optionsPage.selectedList.map((c) => c.createAPIModel()),
      tags: this.optionsPage.selectedTag.map((c) => c.createAPIModel()),
      interests: [],
    };
  }

  constructStoreFromAPIModel(data: IAPICheckOutSaveUpdateModel) {
    this.detailPage = CheckoutDetailPageModel.deserializeAPIModel(data);
    // Exempt the saved seller from the availability reset - see the field's comment.
    this.sellerTermsBaselineSellerId =
      this.detailPage.selectedSeller?.id || null;
    this.sellPage = CheckOutSellPageModel.deserializeAPIModel(
      data.sellDetails,
      data.customOption,
    );
    this.afterPayPage = CheckOutAfterPayModel.deserializeAPIModel(
      data.afterPayDetails,
    );
    this.paymentPageData = CheckoutPaymentPageModel.deserializeAPIModel(
      data.payDetails,
    );
    // Snapshot as loaded - see the fields' comment for why this is not read live.
    this.sellerTermsWasConfigured =
      this.paymentPageData.sellerTermsAndConditions !== "DoNotDisplay";
    this.sellerReturnPolicyWasConfigured =
      this.paymentPageData.sellerReturnPolicy !== "DoNotDisplay";
    if (data.isDonationOnly) {
      this.optionsPage = CheckoutOptionsPageModel.deserializeDonationData(
        data.options,
        data.media || new MediaModel(),
        data.mediaPlacement,
      );
      if (this.sellPage.items?.length > 0) {
        const donationItems = this.sellPage.items[0];
        if (
          donationItems.itemDetails?.type === "Donation" &&
          donationItems.itemDetails.donationDetailPageData &&
          donationItems.itemDetails.donationPriceSettings
        ) {
          this.updateDonationData(
            donationItems.itemDetails.donationDetailPageData,
            donationItems.itemDetails.donationPriceSettings,
            true,
          );
        }
      }
    } else {
      this.optionsPage = CheckoutOptionsPageModel.deserializeAPIModel(
        data.options,
        data.lists,
        data.tags,
      );
    }
  }
}

export class CheckoutRelatedGridModel {
  id: string;
  isActive: boolean;
  name: string;
  lastPurchase: string;
  minPrice: number;
  maxPrice: number;
  constructor(data?: Partial<CheckoutRelatedGridModel>) {
    this.id = data?.id || "";
    this.isActive = data?.isActive || false;
    this.name = data?.name || "";
    this.lastPurchase = data?.lastPurchase || "";
    this.minPrice = data?.minPrice || 0;
    this.maxPrice = data?.maxPrice || 0;
  }

  static deserializeAPIModel(
    data: IAPIRelatedCheckoutGridModel,
  ): CheckoutRelatedGridModel {
    const mappedData: Partial<CheckoutRelatedGridModel> = {
      id: data.id,
      isActive: data.isActive,
      name: data.name,
      lastPurchase: data.lastPurchaseDate
        ? new Date(data.lastPurchaseDate).toLocaleDateString()
        : "Never",
      minPrice: data.minPrice,
      maxPrice: data.maxPrice,
    };

    return new CheckoutRelatedGridModel(mappedData);
  }
}
